// src/components/LetterEditor.tsx
// ──────────────────────────────────────────────────────────────
// Rich text editor for prior auth / appeal letters using Tiptap.
// Converts plain-text letter output into structured HTML, then
// lets physicians format with bold, italic, headings, and lists.
// ──────────────────────────────────────────────────────────────

import { useEffect, useCallback, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";

// ── Types ────────────────────────────────────────────────────

interface LetterEditorProps {
  /** Plain-text letter content from template engine */
  content: string;
  /** Read-only preview mode vs. editable */
  editable: boolean;
  /** Called on every edit with updated HTML */
  onUpdate?: (html: string) => void;
  /** Ref exposed for parent to extract content */
  editorRef?: React.MutableRefObject<{
    getHTML: () => string;
    getText: () => string;
  } | null>;
}

// ── Plain text → HTML converter ──────────────────────────────
// Detects section headers (ALL CAPS lines), preserves paragraphs,
// and converts line breaks into proper HTML structure.

function plainTextToHTML(text: string): string {
  const lines = text.split("\n");
  const htmlParts: string[] = [];
  let inParagraph = false;
  let paragraphLines: string[] = [];

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      htmlParts.push(`<p>${paragraphLines.join("<br>")}</p>`);
      paragraphLines = [];
    }
    inParagraph = false;
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Empty line — flush paragraph
    if (!trimmed) {
      flushParagraph();
      continue;
    }

    // Detect section headers: ALL CAPS, 3+ chars, no punctuation ending
    // e.g. "MEDICAL NECESSITY", "CRITERIA MET", "SUPPORTING EVIDENCE"
    if (
      /^[A-Z][A-Z\s]{2,}$/.test(trimmed) &&
      trimmed.length >= 3 &&
      !trimmed.endsWith(".")
    ) {
      flushParagraph();
      htmlParts.push(`<h2>${trimmed}</h2>`);
      continue;
    }

    // Detect sub-headers: "RE: ..." or "Patient:" etc.
    if (/^(RE|Patient|Date of Birth|Member ID|Plan|Service Requested|Date of Service|Diagnosis|NPI):/.test(trimmed)) {
      flushParagraph();
      // Bold the label portion
      const colonIdx = trimmed.indexOf(":");
      const label = trimmed.slice(0, colonIdx + 1);
      const value = trimmed.slice(colonIdx + 1).trim();
      htmlParts.push(`<p><strong>${label}</strong> ${value}</p>`);
      continue;
    }

    // Numbered citations: "1. ...", "2. ..." — likely in criteria/references
    if (/^\d+\.\s/.test(trimmed)) {
      // If we were in a paragraph, flush it
      if (inParagraph && paragraphLines.some(l => !/^\d+\.\s/.test(l.trim()))) {
        flushParagraph();
      }
      paragraphLines.push(trimmed);
      inParagraph = true;
      continue;
    }

    // Bullet points: "- ..." or "• ..."
    if (/^[-•]\s/.test(trimmed)) {
      flushParagraph();
      htmlParts.push(`<li>${trimmed.replace(/^[-•]\s/, "")}</li>`);
      continue;
    }

    // Regular text — accumulate into paragraph
    paragraphLines.push(trimmed);
    inParagraph = true;
  }

  flushParagraph();

  // Wrap consecutive <li> in <ul>
  let html = htmlParts.join("\n");
  html = html.replace(/(<li>.*?<\/li>\n?)+/g, (match) => `<ul>${match}</ul>`);

  return html;
}

// ── HTML → plain text converter ──────────────────────────────

function htmlToPlainText(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html;

  let text = "";

  function walk(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      text += node.textContent;
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    // Block elements get newlines
    if (["p", "div", "h1", "h2", "h3", "h4", "li"].includes(tag)) {
      if (text.length > 0 && !text.endsWith("\n\n")) {
        text += text.endsWith("\n") ? "\n" : "\n\n";
      }
    }

    if (tag === "br") {
      text += "\n";
      return;
    }

    if (tag === "li") {
      text += "• ";
    }

    for (const child of Array.from(node.childNodes)) {
      walk(child);
    }

    if (["p", "div", "h1", "h2", "h3", "h4"].includes(tag)) {
      if (!text.endsWith("\n")) text += "\n";
    }
  }

  walk(div);
  return text.trim();
}

// ── Toolbar icons ────────────────────────────────────────────

function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-1.5 rounded transition-colors ${
        active
          ? "bg-teal-100 text-teal-700"
          : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"
      } disabled:opacity-30 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

function IconBold() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6V4zm0 8h9a4 4 0 014 4 4 4 0 01-4 4H6v-8z" stroke="currentColor" strokeWidth={0.5} fill="none" />
      <path d="M8 6v4h5a2 2 0 000-4H8zm0 6v4h6a2 2 0 000-4H8z" fill="currentColor" />
    </svg>
  );
}

function IconItalic() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <line x1="19" y1="4" x2="10" y2="4" />
      <line x1="14" y1="20" x2="5" y2="20" />
      <line x1="15" y1="4" x2="9" y2="20" />
    </svg>
  );
}

function IconUnderline() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M6 3v7a6 6 0 006 6 6 6 0 006-6V3" />
      <line x1="4" y1="21" x2="20" y2="21" />
    </svg>
  );
}

function IconHeading() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path d="M6 4v16M18 4v16M6 12h12" />
    </svg>
  );
}

function IconBulletList() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <line x1="9" y1="6" x2="20" y2="6" />
      <line x1="9" y1="12" x2="20" y2="12" />
      <line x1="9" y1="18" x2="20" y2="18" />
      <circle cx="4.5" cy="6" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="12" r="1.5" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="18" r="1.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function IconOrderedList() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <line x1="10" y1="6" x2="20" y2="6" />
      <line x1="10" y1="12" x2="20" y2="12" />
      <line x1="10" y1="18" x2="20" y2="18" />
      <text x="3" y="8" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">1</text>
      <text x="3" y="14" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">2</text>
      <text x="3" y="20" fontSize="8" fill="currentColor" stroke="none" fontFamily="sans-serif">3</text>
    </svg>
  );
}

function IconUndo() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
    </svg>
  );
}

function IconRedo() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l6-6m0 0l-6-6m6 6H9a6 6 0 000 12h3" />
    </svg>
  );
}

// ── Toolbar ──────────────────────────────────────────────────

function EditorToolbar({ editor }: { editor: ReturnType<typeof useEditor> }) {
  if (!editor) return null;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/50 flex-wrap">
      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        title="Bold (⌘B)"
      >
        <IconBold />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        title="Italic (⌘I)"
      >
        <IconItalic />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        active={editor.isActive("underline")}
        title="Underline (⌘U)"
      >
        <IconUnderline />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-200 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive("heading", { level: 2 })}
        title="Section Header"
      >
        <IconHeading />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-200 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        title="Bullet List"
      >
        <IconBulletList />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        title="Numbered List"
      >
        <IconOrderedList />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-200 mx-1" />

      <ToolbarButton
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        title="Undo (⌘Z)"
      >
        <IconUndo />
      </ToolbarButton>

      <ToolbarButton
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        title="Redo (⌘⇧Z)"
      >
        <IconRedo />
      </ToolbarButton>
    </div>
  );
}

// ── Editor Component ─────────────────────────────────────────

export function LetterEditor({
  content,
  editable,
  onUpdate,
  editorRef,
}: LetterEditorProps) {
  const initialHTMLRef = useRef<string | null>(null);

  // Convert plain text to HTML on first load or content change
  const html = plainTextToHTML(content);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
    ],
    content: html,
    editable,
    editorProps: {
      attributes: {
        class: "prose prose-sm prose-slate max-w-none focus:outline-none min-h-[300px] px-5 py-4",
      },
    },
    onUpdate: ({ editor: e }) => {
      onUpdate?.(e.getHTML());
    },
  });

  // Sync editable prop
  useEffect(() => {
    if (editor) {
      editor.setEditable(editable);
    }
  }, [editor, editable]);

  // Update content when it changes externally (template switch, regenerate)
  useEffect(() => {
    if (editor && content) {
      const newHTML = plainTextToHTML(content);
      if (newHTML !== initialHTMLRef.current) {
        initialHTMLRef.current = newHTML;
        editor.commands.setContent(newHTML);
      }
    }
  }, [editor, content]);

  // Expose methods to parent
  useEffect(() => {
    if (editorRef && editor) {
      editorRef.current = {
        getHTML: () => editor.getHTML(),
        getText: () => htmlToPlainText(editor.getHTML()),
      };
    }
  }, [editor, editorRef]);

  return (
    <div
      className={`rounded-lg border overflow-hidden transition-colors ${
        editable
          ? "border-teal-200 bg-white ring-1 ring-teal-100"
          : "border-slate-200 bg-slate-50/30"
      }`}
    >
      {/* Toolbar — only in edit mode */}
      {editable && <EditorToolbar editor={editor} />}

      {/* Editor content */}
      <div className="max-h-[500px] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

// ── Exports for parent components ────────────────────────────

export { plainTextToHTML, htmlToPlainText };